import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, ArrowLeft, ArrowRight, Loader2, Trophy, Sparkles, BookOpen, Shield, LineChart, Brain, FileText, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  grundlagen: BookOpen,
  risiko: Shield,
  charts: LineChart,
  psychologie: Brain,
  steuer: FileText,
};

const LEVEL_LABEL = ["Noch nicht bearbeitet", "Einsteiger", "Grundverständnis", "Fortgeschritten", "Experte"];
const LEVEL_COLOR = [
  "bg-secondary border border-border text-muted-foreground",
  "bg-[hsl(32_60%_55%/0.15)] text-[hsl(32_60%_72%)] border border-[hsl(32_60%_55%/0.4)]",
  "bg-[hsl(48_82%_55%/0.15)] text-[hsl(48_82%_72%)] border border-[hsl(48_82%_55%/0.4)]",
  "bg-[hsl(200_66%_55%/0.15)] text-[hsl(200_66%_72%)] border border-[hsl(200_66%_55%/0.4)]",
  "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)] border border-[hsl(122_39%_49%/0.35)]",
];

// Übersicht aller Module + Level pro Modul
export function QuizOverview() {
  const [modules, setModules] = useState(null);
  const [stage, setStage] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/quiz/modules`, { withCredentials: true });
      setModules(r.data.modules); setStage(r.data.stage);
    } catch (e) { toast.error("Quiz konnte nicht geladen werden"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const completedCount = modules?.filter((m) => (m.level || 0) > 0).length ?? 0;
  const total = modules?.length ?? 0;
  const isOrientierung = stage === "orientierung";

  return (
    <div data-testid="quiz-overview">
      <PageHeader
        eyebrow="Wissens-Quiz"
        title="Wo stehst du?"
        description={isOrientierung
          ? "Fünf kurze Themengebiete. Der KI-Coach passt sich anschließend deinem Wissensstand an — je präziser dein Profil, desto besser wirst du abgeholt."
          : "Optional. Wenn du magst, spielst du hier fünf kurze Module durch. Der Coach nutzt dein Wissensprofil, um Fachbegriffe passend zu erklären oder wegzulassen."}
        testid="quiz-header"
      />

      {modules === null && (
        <div className="card-surface p-10 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      )}

      {modules !== null && (
        <>
          <div className="mb-6 text-sm text-muted-foreground" data-testid="quiz-progress">
            {completedCount === 0 && "Noch keine Module bearbeitet."}
            {completedCount > 0 && completedCount < total && `${completedCount} von ${total} Modulen bearbeitet.`}
            {completedCount === total && completedCount > 0 && "Alle Module bearbeitet — jederzeit wiederholbar."}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {modules.map((m) => {
              const Icon = MODULE_ICONS[m.slug] || Sparkles;
              const done = (m.level || 0) > 0;
              return (
                <Link
                  key={m.slug}
                  to={`/app/quiz/${m.slug}`}
                  className="card-surface p-5 flex items-start gap-4 hover:bg-secondary/40 transition-colors group"
                  data-testid={`quiz-module-${m.slug}`}
                >
                  <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center shrink-0">
                    <Icon className="h-5 w-5 text-primary-foreground/80" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-base">{m.label}</h3>
                      <Badge variant="secondary" className={`rounded-full text-[10px] ${LEVEL_COLOR[m.level || 0]}`}>
                        {LEVEL_LABEL[m.level || 0]}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{m.description}</p>
                    <div className="text-[11px] text-muted-foreground mt-2">
                      {done ? `Neu starten · ${m.questions_per_run} Fragen adaptiv` : `Start · ${m.questions_per_run} Fragen adaptiv`}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0 mt-1" strokeWidth={1.5} />
                </Link>
              );
            })}
          </div>

          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Button asChild variant="outline" className="rounded-full" data-testid="quiz-back-to-app">
              <Link to="/app"><ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} /> Zurück zum Dashboard</Link>
            </Button>
            {!isOrientierung && completedCount === 0 && (
              <span className="text-xs text-muted-foreground">Nicht dein Ding? Kein Problem — du kannst das jederzeit später machen.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Ein einzelnes Modul — adaptiver Fragefluss + Auswertung
export function QuizModule() {
  const { module } = useParams();
  const nav = useNavigate();
  const { refresh, simMode } = useAuth() || {};
  const [pool, setPool] = useState(null);      // Alle Fragen des Moduls (ohne Lösung)
  const [asked, setAsked] = useState([]);      // IDs der bereits gestellten Fragen
  const [current, setCurrent] = useState(null);
  const [difficulty, setDifficulty] = useState("medium");
  const [answers, setAnswers] = useState([]);  // {q_id, chosen, is_correct}
  const [feedback, setFeedback] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const QUESTIONS_PER_RUN = 4;

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/quiz/questions/${module}`, { withCredentials: true });
      setPool(r.data.questions);
    } catch { toast.error("Modul konnte nicht geladen werden"); nav("/app/quiz"); }
  }, [module, nav]);
  useEffect(() => { load(); }, [load]);

  const pickNext = useCallback((wantedDifficulty, alreadyAsked) => {
    if (!pool) return null;
    // Bevorzugt die gewünschte Schwierigkeit; fällt auf eine andere zurück, wenn ausgeschöpft.
    const buckets = ["easy", "medium", "hard"];
    const primary = pool.filter((q) => q.difficulty === wantedDifficulty && !alreadyAsked.includes(q.id));
    if (primary.length) return primary[Math.floor(Math.random() * primary.length)];
    // Fallback: nächstnahe Schwierigkeit
    for (const d of buckets) {
      if (d === wantedDifficulty) continue;
      const alt = pool.filter((q) => q.difficulty === d && !alreadyAsked.includes(q.id));
      if (alt.length) return alt[Math.floor(Math.random() * alt.length)];
    }
    return null;
  }, [pool]);

  // Erste Frage einwerfen, sobald Pool geladen
  useEffect(() => {
    if (pool && !current && asked.length === 0 && !result) {
      const q = pickNext("medium", []);
      if (q) { setCurrent(q); }
    }
  }, [pool, current, asked, pickNext, result]);

  const submitAll = useCallback(async (allAnswers) => {
    setSubmitting(true);
    // Simulationsmodus (Admin): keine DB-Persistenz. Wir werten das Quiz
    // rein lokal aus, damit der Admin die Ergebnis-Seite exakt so sieht wie
    // ein echter Nutzer — ohne dabei sein reales knowledge_profile zu
    // überschreiben.
    if (simMode) {
      const correct = allAnswers.filter((a) => a.is_correct).length;
      const total = allAnswers.length;
      const level = correct >= 4 ? 4 : correct >= 3 ? 3 : correct >= 2 ? 2 : 1;
      const review = allAnswers.map((a) => ({
        q_id: a.q_id,
        chosen: a.chosen,
        is_correct: a.is_correct,
        correct_index: a.correct_index,
        chose_dont_know: a.chose_dont_know,
        question: (pool || []).find((q) => q.id === a.q_id)?.q || "",
        explanation: a.explanation || "",
      }));
      setResult({ level, correct, total, review, sim: true });
      setSubmitting(false);
      return;
    }
    try {
      const r = await axios.post(`${API}/quiz/submit`, { module, answers: allAnswers.map((a) => ({ q_id: a.q_id, chosen: a.chosen })) }, { withCredentials: true });
      setResult(r.data);
      if (refresh) refresh();
    } catch (e) {
      toast.error("Auswertung fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setSubmitting(false); }
  }, [module, refresh, simMode, pool]);

  const answer = async (idx) => {
    if (!current || submitting || checking || feedback) return;
    setChecking(true);
    try {
      const r = await axios.post(`${API}/quiz/check`,
        { module, q_id: current.id, chosen: idx },
        { withCredentials: true });
      setFeedback({ ...r.data, chosen: idx });
    } catch (e) {
      toast.error("Antwort konnte nicht geprüft werden");
    } finally { setChecking(false); }
  };

  const proceed = () => {
    if (!feedback || !current) return;
    const next = {
      q_id: current.id,
      chosen: feedback.chosen,
      is_correct: feedback.is_correct,
      correct_index: feedback.correct_index,
      chose_dont_know: feedback.chose_dont_know,
      explanation: feedback.explanation,
    };
    const newAsked = [...asked, current.id];
    const newAnswers = [...answers, next];
    setAsked(newAsked);
    setAnswers(newAnswers);
    setFeedback(null);

    if (newAnswers.length >= QUESTIONS_PER_RUN) {
      setCurrent(null);
      submitAll(newAnswers);
      return;
    }
    const seq = ["medium", "hard", "medium", "easy"];
    const nextDifficulty = seq[newAnswers.length] || "medium";
    setDifficulty(nextDifficulty);
    const nextQ = pickNext(nextDifficulty, newAsked);
    setCurrent(nextQ);
  };

  const label = MODULES_LABEL[module] || module;
  const Icon = MODULE_ICONS[module] || Sparkles;

  return (
    <div data-testid={`quiz-module-page-${module}`}>
      <PageHeader
        eyebrow="Wissens-Quiz"
        title={label}
        description="Vier Fragen adaptiv. Kein Zeitdruck, keine Bewertung im schlechten Sinn — es geht nur darum, dass der Coach dich richtig einschätzen kann."
        testid={`quiz-module-header-${module}`}
      />

      {pool === null && (
        <div className="card-surface p-10 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
        </div>
      )}

      {pool !== null && current && !result && (
        <div className="card-surface p-6 md:p-8 max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-full bg-secondary border border-border grid place-items-center">
              <Icon className="h-4 w-4 text-primary-foreground/80" strokeWidth={1.5} />
            </div>
            <div className="text-xs text-muted-foreground">
              Frage {answers.length + 1} / {QUESTIONS_PER_RUN} · {current.difficulty === "easy" ? "leicht" : current.difficulty === "medium" ? "mittel" : "schwer"}
            </div>
          </div>
          <h2 className="font-heading text-xl leading-snug" data-testid="quiz-question-text">{current.q}</h2>
          <div className="mt-6 grid grid-cols-1 gap-2">
            {current.options.map((opt, i) => {
              const disabled = checking || !!feedback;
              const isChosen = feedback && feedback.chosen === i;
              const isCorrect = feedback && feedback.correct_index === i;
              let cls = "border-border bg-secondary/40 hover:bg-secondary hover:border-primary/50";
              if (feedback) {
                if (isCorrect) cls = "border-[hsl(122_39%_49%/0.5)] bg-[hsl(122_39%_49%/0.1)]";
                else if (isChosen) cls = "border-[hsl(0_66%_50%/0.5)] bg-[hsl(0_66%_50%/0.08)]";
                else cls = "border-border bg-secondary/20 opacity-60";
              }
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => answer(i)}
                  disabled={disabled}
                  className={`text-left rounded-xl border transition-colors px-4 py-3 text-sm ${cls} ${disabled ? "cursor-default" : ""}`}
                  data-testid={`quiz-option-${i}`}
                >
                  <span className="text-muted-foreground mr-2">{String.fromCharCode(65 + i)}.</span>
                  {opt}
                  {feedback && isCorrect && <CheckCircle2 className="inline-block ml-2 h-4 w-4 text-[hsl(122_39%_65%)]" strokeWidth={1.75} />}
                  {feedback && isChosen && !isCorrect && <XCircle className="inline-block ml-2 h-4 w-4 text-[hsl(0_66%_72%)]" strokeWidth={1.75} />}
                </button>
              );
            })}
          </div>

          {feedback && (
            <div
              className={`mt-5 rounded-xl border p-4 ${feedback.is_correct
                ? "border-[hsl(122_39%_49%/0.3)] bg-[hsl(122_39%_49%/0.06)]"
                : "border-[hsl(0_66%_50%/0.3)] bg-[hsl(0_66%_50%/0.06)]"}`}
              data-testid="quiz-inline-feedback"
            >
              <div className="flex items-start gap-2">
                {feedback.is_correct
                  ? <CheckCircle2 className="h-4 w-4 text-[hsl(122_39%_65%)] mt-0.5 shrink-0" strokeWidth={1.75} />
                  : <XCircle className="h-4 w-4 text-[hsl(0_66%_72%)] mt-0.5 shrink-0" strokeWidth={1.75} />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">
                    {feedback.is_correct ? "Richtig" : (feedback.chose_dont_know ? "Ehrlich — passt" : "Nicht ganz")}
                  </div>
                  {!feedback.is_correct && (
                    <div className="text-sm text-foreground mt-1" data-testid="quiz-inline-correct-option">
                      {feedback.chose_dont_know
                        ? <>Richtige Antwort: <span className="font-medium">{feedback.correct_option}</span></>
                        : <>Richtig wäre <span className="font-medium">{String.fromCharCode(65 + feedback.correct_index)}</span>: {feedback.correct_option}</>}
                    </div>
                  )}
                  <div className="text-sm text-foreground/90 mt-2 leading-relaxed" data-testid="quiz-inline-explanation">
                    {feedback.explanation}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={proceed} className="rounded-full" data-testid="quiz-next">
                  {answers.length + 1 >= QUESTIONS_PER_RUN
                    ? <>Auswerten <ChevronRight className="ml-1 h-4 w-4" strokeWidth={1.5} /></>
                    : <>Nächste Frage <ChevronRight className="ml-1 h-4 w-4" strokeWidth={1.5} /></>}
                </Button>
              </div>
            </div>
          )}

          {checking && !feedback && (
            <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> Wird geprüft…
            </div>
          )}
        </div>
      )}

      {submitting && (
        <div className="card-surface p-10 text-center">
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-muted-foreground">Antworten werden ausgewertet…</p>
        </div>
      )}

      {result && (
        <div className="max-w-3xl" data-testid="quiz-result">
          <div className="card-surface p-6 md:p-8">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="h-11 w-11 rounded-full bg-secondary border border-border grid place-items-center">
                <Trophy className="h-5 w-5 text-[hsl(48_82%_72%)]" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Ergebnis {label}</div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <h2 className="font-heading text-2xl">{LEVEL_LABEL[result.level]}</h2>
                  <Badge variant="secondary" className={`rounded-full text-xs ${LEVEL_COLOR[result.level]}`}>Level {result.level}/4</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{result.correct} von {result.total} Fragen richtig · Dein Coach nutzt das ab sofort.</p>
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {result.review.map((r, i) => (
                <div key={i} className="rounded-xl border border-border bg-secondary/30 p-4" data-testid={`quiz-review-${i}`}>
                  <div className="flex items-start gap-2">
                    {r.is_correct
                      ? <CheckCircle2 className="h-4 w-4 text-[hsl(122_39%_65%)] mt-0.5 shrink-0" strokeWidth={1.5} />
                      : <XCircle className="h-4 w-4 text-[hsl(0_66%_72%)] mt-0.5 shrink-0" strokeWidth={1.5} />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-foreground">{r.explanation}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <Button asChild className="rounded-full" data-testid="quiz-back-overview">
                <Link to="/app/quiz">Weiter zum nächsten Modul</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MODULES_LABEL = {
  grundlagen: "Grundlagen",
  risiko: "Risiko-Management",
  charts: "Chart-Lesen",
  psychologie: "Trading-Psychologie",
  steuer: "Steuer (Deutschland)",
};

export default QuizOverview;
